/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { MouseEvent, useState } from 'react';
import { usePublishEvent } from '@agilelab/plugin-wb-events';
import { userSignedOutTopic } from '@agilelab/plugin-wb-events-common';
import {
  configApiRef,
  errorApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { IconButton, ListItemIcon, Menu, MenuItem } from '@material-ui/core';
import SignOutIcon from '@material-ui/icons/MeetingRoom';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { authExtensionApiRef } from '@agilelab/plugin-wb-auth-extension-common';

/** @public */
export const UserSettingsMenu = () => {
  const errorApi = useApi(errorApiRef);
  const identityApi = useApi(identityApiRef);
  const authExtensionApi = useApi(authExtensionApiRef);
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<undefined | HTMLElement>(undefined);
  const config = useApi(configApiRef);
  const { publish } = usePublishEvent({ generatorSystem: 'settings' });
  const shouldInvalidateTokensOnLogout = config.getOptionalBoolean(
    'auth.tokenInvalidation.invalidateOnLogout',
  );

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(undefined);
    setOpen(false);
  };

  return (
    <>
      <IconButton
        data-testid="user-settings-menu"
        aria-label="more"
        onClick={handleOpen}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem
          data-testid="sign-out"
          onClick={async () => {
            await Promise.allSettled(
              [
                // Publish user signed out event
                publish(userSignedOutTopic, {}),
                // Invalidate all user tokens issued before the current time
                shouldInvalidateTokensOnLogout
                  ? authExtensionApi.invalidateUserTokens({})
                  : [],
              ].flat(),
            );

            identityApi.signOut().catch(error => errorApi.post(error));
          }}
        >
          <ListItemIcon>
            <SignOutIcon />
          </ListItemIcon>
          Sign Out
        </MenuItem>
      </Menu>
    </>
  );
};
